// The phone's frame: the page scrolls between the safe areas, over the tab bar (Today, Calendar, the orb with what
// is waiting for you, Spaces, More). The Activity page has its own dock instead, and the assistant is full screen
// with its own bar, so neither has the tab bar.
// The orb (Kareem, 5 Nov): a tap opens the Assistant as before; holding it for 2 seconds starts voice control.
import { useRef, type ReactNode } from 'react';
import { useVoiceControl } from '../voice/VoiceControl';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { isSuggestion } from '@dpw/shared';
import { useActions, useChecksToAnswer } from '../lib/queries';
import { CommandPalette } from '../ui/CommandPalette';
import { Glide } from '../ui/motion';
import { P } from './kit';
import { useKeyboardInset } from './keyboard';

const SPACES = /^\/(spaces|tracks)(\/|$)/;
const MORE = /^\/(more|requests|notifications|settings|reminders|knowledge|folders|files|checks|search|meetings|record)(\/|$)/;

/** What is waiting for you: cards to approve and questions to answer (context suggestions are optional). */
export function useWaitingCount() {
  const acts = useActions('waiting');
  const asks = useChecksToAnswer();
  return (acts.data ?? []).filter(a => a.status === 'waiting' && !isSuggestion(a)).length
    + (asks.data ?? []).filter(c => c.view === 'full' && c.status === 'open').length;
}

export function PhoneShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  // Talk it through scrolls like a page (no tab bar while talking); the chat itself is full screen.
  const talk = pathname.startsWith('/assistant/talk');
  const full = pathname.startsWith('/assistant') && !talk;
  const dock = pathname.startsWith('/activities/');
  const n = useWaitingCount();
  const vc = useVoiceControl();
  // Hold for voice control: 2 seconds down starts it, and the release that ends the hold does not also open the Assistant.
  const hold = useRef<{ t: number | null; fired: boolean }>({ t: null, fired: false });
  const down = () => { hold.current.fired = false; hold.current.t = window.setTimeout(() => { hold.current.fired = true; vc.start(); }, 2000); };
  const up = () => { if (hold.current.t !== null) window.clearTimeout(hold.current.t); hold.current.t = null; };
  useKeyboardInset();
  const tab = (on: boolean) => (on ? 'on' : '');
  return (
    <div className={`ph${full ? ' full notab' : dock ? ' dock notab' : talk ? ' notab' : ''}`}>
      <Glide as="main" className="ph-main">{children}</Glide>
      {!full && !dock && !talk && (
        <nav className="ph-tb" aria-label="Main">
          <NavLink to="/" end className={({ isActive }) => tab(isActive)}><P.home />Today</NavLink>
          <NavLink to="/calendar" className={({ isActive }) => tab(isActive)}><P.cal />Calendar</NavLink>
          <Link className="ph-orbt" to="/assistant" aria-label={n ? `Assistant, ${n} waiting for you` : 'Assistant'} title="Assistant. Hold for voice control"
            onClick={e => { if (hold.current.fired) { e.preventDefault(); hold.current.fired = false; } }}
            onPointerDown={down} onPointerUp={up} onPointerLeave={up} onPointerCancel={up} onContextMenu={e => e.preventDefault()} draggable={false}>
            <span className="o">{n > 0 && <span className="ph-obadge" aria-hidden="true">{n}</span>}<P.spark /></span>
          </Link>
          <Link to="/spaces" className={tab(SPACES.test(pathname))} aria-current={SPACES.test(pathname) ? 'page' : undefined}><P.grid />Spaces</Link>
          <Link to="/more" className={tab(MORE.test(pathname))} aria-current={MORE.test(pathname) ? 'page' : undefined}><P.more />More</Link>
        </nav>
      )}
      <CommandPalette />
    </div>
  );
}
